import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class SkincareProtocols extends UcoastEl {
	static htmlSelector = 'skincare-protocols'

	private protocolToggles: HTMLButtonElement[]
	private protocolPanels: HTMLElement[]
	private protocolsContainer?: HTMLElement
	private tipsToggles: HTMLButtonElement[]
	private protocolTransitionTimeout: number | undefined

	constructor() {
		super()

		this.protocolToggles = Array.from(
			this.querySelectorAll<HTMLButtonElement>('[data-skincare-protocol-toggle]')
		)
		this.protocolPanels = Array.from(
			this.querySelectorAll<HTMLElement>('[data-skincare-protocol-panel]')
		)
		this.protocolsContainer = this.querySelector<HTMLElement>(
			'.skincare-protocols__protocols'
		) ?? undefined
		this.tipsToggles = Array.from(
			this.querySelectorAll<HTMLButtonElement>('[data-skincare-tips-toggle]')
		)
	}

	override connectedCallback() {
		super.connectedCallback()

		if (!this.protocolToggles.length || !this.protocolPanels.length) return

		this.protocolToggles.forEach((toggle) =>
			toggle.addEventListener('click', this.handleProtocolToggleClick)
		)
		this.tipsToggles.forEach((toggle) =>
			toggle.addEventListener('click', this.handleTipsToggleClick)
		)

		this.protocolPanels.forEach((panel) => {
			this.getStepDetails(panel).forEach((details) => {
				details.addEventListener('toggle', this.handleStepToggle)
			})
		})

		const initialProtocolIndex = this.protocolToggles.findIndex((toggle) =>
			toggle.classList.contains('is-active')
		)

		this.activateProtocol(
			initialProtocolIndex >= 0 ? initialProtocolIndex : 0
		)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()

		this.protocolToggles.forEach((toggle) =>
			toggle.removeEventListener('click', this.handleProtocolToggleClick)
		)
		this.tipsToggles.forEach((toggle) =>
			toggle.removeEventListener('click', this.handleTipsToggleClick)
		)

		this.protocolPanels.forEach((panel) => {
			this.getStepDetails(panel).forEach((details) => {
				details.removeEventListener('toggle', this.handleStepToggle)
			})
		})
	}

	private handleProtocolToggleClick = (event: MouseEvent) => {
		const toggle = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!toggle) return
		if (toggle.classList.contains('is-active')) return

		const protocolIndex = Number(toggle.dataset.protocolIndex)
		if (Number.isNaN(protocolIndex)) return

		this.activateProtocol(protocolIndex)
	}

	private handleStepToggle = (event: Event) => {
		const details = event.currentTarget
		if (!(details instanceof HTMLDetailsElement)) return

		const summary = this.getStepSummary(details)
		summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false')

		if (!details.open) return

		const panel = details.closest<HTMLElement>('[data-skincare-protocol-panel]')
		if (!panel) return

		const stepIndex = Number(details.dataset.stepIndex)
		if (Number.isNaN(stepIndex)) return

		this.syncActiveStep(panel, stepIndex)
	}

	private handleTipsToggleClick = (event: MouseEvent) => {
		const toggle = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!toggle) return

		const tips = toggle.closest<HTMLElement>('.skincare-protocols__tips')
		const body = tips?.querySelector<HTMLElement>('[data-skincare-tips-body]')
		if (!tips || !body) return

		const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
		toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
		tips.classList.toggle('is-collapsed', isExpanded)
		body.hidden = isExpanded
	}

	private activateProtocol(protocolIndex: number) {
		if (!this.protocolsContainer) {
			this.setActiveProtocol(protocolIndex)
			return
		}

		window.clearTimeout(this.protocolTransitionTimeout)
		this.protocolsContainer.classList.add('is-fading-out')
		this.protocolsContainer.classList.remove('is-fading-in')

		this.protocolTransitionTimeout = window.setTimeout(() => {
			this.setActiveProtocol(protocolIndex)
			this.protocolsContainer?.classList.remove('is-fading-out')
			this.protocolsContainer?.classList.add('is-fading-in')

			this.protocolTransitionTimeout = window.setTimeout(() => {
				this.protocolsContainer?.classList.remove('is-fading-in')
			}, 280)
		}, 180)
	}

	private setActiveProtocol(protocolIndex: number) {
		this.protocolToggles.forEach((toggle, toggleIndex) => {
			const isActive = toggleIndex === protocolIndex
			toggle.classList.toggle('is-active', isActive)
			toggle.setAttribute('aria-pressed', isActive ? 'true' : 'false')
		})

		this.protocolPanels.forEach((panel, panelIndex) => {
			const isActive = panelIndex === protocolIndex
			panel.classList.toggle('is-active', isActive)
			panel.hidden = !isActive
		})

		const activePanel = this.protocolPanels[protocolIndex]
		if (!activePanel) return

		const currentStepIndex = Number(activePanel.dataset.currentStepIndex)
		const defaultStepIndex = Number(activePanel.dataset.defaultStepIndex)
		const nextStepIndex = Number.isNaN(currentStepIndex)
			? defaultStepIndex || 0
			: currentStepIndex

		this.setOpenStep(activePanel, nextStepIndex)
	}

	private setOpenStep(panel: HTMLElement, stepIndex: number) {
		this.getStepDetails(panel).forEach((details) => {
			const detailsStepIndex = Number(details.dataset.stepIndex)
			const isActive = detailsStepIndex === stepIndex

			if (isActive) {
				details.setAttribute('open', '')
			} else {
				details.removeAttribute('open')
			}

			this.getStepSummary(details)?.setAttribute(
				'aria-expanded',
				isActive ? 'true' : 'false'
			)
		})

		this.syncActiveStep(panel, stepIndex)
	}

	private syncActiveStep(panel: HTMLElement, stepIndex: number) {
		panel.dataset.currentStepIndex = String(stepIndex)

		this.getMediaPanels(panel).forEach((media) => {
			const mediaStepIndex = Number(media.dataset.stepIndex)
			const isActive = mediaStepIndex === stepIndex
			media.classList.toggle('is-active', isActive)
			media.hidden = !isActive
		})

		this.getFeaturedCards(panel).forEach((card) => {
			const cardStepIndex = Number(card.dataset.stepIndex)
			const isActive = cardStepIndex === stepIndex
			card.classList.toggle('is-active', isActive)
			card.hidden = !isActive
		})
	}

	private getStepDetails(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLDetailsElement>('[data-skincare-step-details]')
		)
	}

	private getStepSummary(details: HTMLDetailsElement) {
		return details.querySelector<HTMLElement>('summary')
	}

	private getMediaPanels(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLElement>('[data-skincare-step-media]')
		)
	}

	private getFeaturedCards(panel: HTMLElement) {
		return Array.from(
			panel.querySelectorAll<HTMLElement>('[data-skincare-step-product]')
		)
	}
}
